// Pure, framework-agnostic focus-trap helpers. Kept free of React and of layout-based
// visibility checks so they can be unit-tested under jsdom (which has no layout engine).

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button',
  'input',
  'select',
  'textarea',
  '[tabindex]',
  '[contenteditable="true"]',
].join(',');

const isDisabled = (el) =>
  el.disabled === true || el.getAttribute('aria-disabled') === 'true';

const isHidden = (el) =>
  el.hidden === true ||
  el.getAttribute('type') === 'hidden' ||
  el.getAttribute('aria-hidden') === 'true';

const hasNegativeTabIndex = (el) => {
  const attr = el.getAttribute('tabindex');
  return attr !== null && Number(attr) < 0;
};

const isFocusableAnchor = (el) =>
  el.tagName !== 'A' || el.hasAttribute('href');

/**
 * Returns the focusable descendants of `container` in document order.
 * Filters out disabled, hidden, tabindex="-1", and href-less anchors.
 */
export function getFocusableElements(container) {
  if (!container || typeof container.querySelectorAll !== 'function') return [];
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (el) =>
      !isDisabled(el) &&
      !isHidden(el) &&
      !hasNegativeTabIndex(el) &&
      isFocusableAnchor(el),
  );
}

/**
 * Given the index of the currently focused element among `count` focusable elements,
 * returns the index that should receive focus next. Wraps around at both ends.
 * `currentIndex` of -1 means focus is currently outside the known set.
 * Returns -1 when there is nothing focusable.
 */
export function nextFocusIndex(currentIndex, count, shiftKey) {
  if (count <= 0) return -1;
  if (shiftKey) {
    if (currentIndex <= 0) return count - 1;
    return currentIndex - 1;
  }
  if (currentIndex === -1 || currentIndex >= count - 1) return 0;
  return currentIndex + 1;
}

export { FOCUSABLE_SELECTOR };
